/**
 * `/settings/logs` — flat audit-run log for developers.
 *
 * Replacement for legacy `AuditsIsland.tsx`. Framed as a
 * developer/observability surface per topology.md §3 and
 * 05-pages.md §5d: copy-audit-id / copy-request-id are primary
 * actions, row click takes the user to the canonical detail page
 * at `/settings/logs/:auditId`.
 *
 * URL search params drive every filter so deep-links work — the
 * Page timeline "Open full audit log →" anchor passes
 * `?pageUuid=<uuid>`.
 */
import { AuditsTable } from '@/components/primitives/AuditsTable';
import { EmptyState } from '@/components/primitives/EmptyState';
import { ErrorState } from '@/components/primitives/ErrorState';
import { PageActions } from '@/components/primitives/PageActions';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Menu, MenuItem, MenuPopup, MenuTrigger } from '@/components/ui/menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import type { AuditResponse } from '@/lib/api-types';
import { toastPromise, toastSuccess } from '@/lib/errors';
import { useAudits, useCreateAudit } from '@/lib/queries';
import {
  ArrowsClockwise,
  Copy,
  DotsThreeVertical,
  DownloadSimple,
  FunnelSimple,
  ListBullets,
  MagnifyingGlass,
} from '@phosphor-icons/react';
import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router';
import { type ReactElement, useMemo, useState } from 'react';

type LogsSearch = {
  pageUuid?: string;
  siteUuid?: string;
  status?: string;
  q?: string;
  from?: string;
  to?: string;
};

export const Route = createFileRoute('/_app/settings/logs/')({
  component: LogsPage,
  validateSearch: (raw: Record<string, unknown>): LogsSearch => {
    const out: LogsSearch = {};
    const keys: (keyof LogsSearch)[] = ['pageUuid', 'siteUuid', 'status', 'q', 'from', 'to'];
    for (const k of keys) {
      const v = raw[k];
      if (typeof v === 'string' && v.length > 0) out[k] = v;
    }
    return out;
  },
});

const PAGE_SIZE = 50;

function LogsPage(): ReactElement {
  const search = useSearch({ from: '/_app/settings/logs/' });
  const navigate = useNavigate();

  const [offset, setOffset] = useState(0);
  const [searchText, setSearchText] = useState(search.q ?? '');

  // Full audits list — we page via `limit`/`offset`. Client-side
  // filtering by pageUuid/siteUuid/q because the server doesn't
  // support those params on GET /v1/audits today.
  const audits = useAudits({ limit: PAGE_SIZE, offset });
  const createAudit = useCreateAudit();

  const filtered = useMemo(() => {
    const rows = audits.data?.audits ?? [];
    let out = rows;
    if (search.pageUuid) {
      out = out.filter((a) => a.ids?.page_uuid === search.pageUuid);
    }
    if (search.siteUuid) {
      out = out.filter((a) => a.ids?.site_uuid === search.siteUuid);
    }
    if (search.status) {
      out = out.filter((a) => a.status === search.status);
    }
    if (search.q) {
      const q = search.q.toLowerCase();
      out = out.filter((a) => a.id.toLowerCase().includes(q) || a.url.toLowerCase().includes(q));
    }
    if (search.from) {
      const f = new Date(search.from).getTime();
      if (!Number.isNaN(f)) {
        out = out.filter((a) => new Date(a.created_at).getTime() >= f);
      }
    }
    if (search.to) {
      const t = new Date(search.to).getTime();
      if (!Number.isNaN(t)) {
        out = out.filter((a) => new Date(a.created_at).getTime() <= t);
      }
    }
    return out;
  }, [audits.data, search]);

  function updateSearch(next: Partial<LogsSearch>) {
    const merged: LogsSearch = { ...search, ...next };
    for (const [k, v] of Object.entries(merged)) {
      if (!v) delete (merged as Record<string, unknown>)[k];
    }
    navigate({ to: '/settings/logs', search: merged, replace: true });
  }

  function applyQ(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    updateSearch({ q: searchText || undefined });
    setOffset(0);
  }

  return (
    <div className="space-y-4">
      <header className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-base font-semibold text-[var(--color-ink)]">Logs</h2>
          <p className="text-sm text-[var(--color-mute)]">
            Every audit run against <span className="font-mono text-xs">/v1/audits</span> in this
            workspace.
          </p>
        </div>
        <PageActions>
          <Button variant="outline" onClick={() => audits.refetch()} aria-label="Refresh">
            <ArrowsClockwise aria-hidden="true" />
            Refresh
          </Button>
        </PageActions>
      </header>

      {(search.pageUuid || search.siteUuid) && (
        <div className="flex items-center gap-2 rounded-lg border border-[var(--color-info-brand)]/40 bg-[var(--color-info-brand)]/5 px-3 py-2 text-sm">
          <FunnelSimple aria-hidden="true" size={14} className="text-[var(--color-accent-brand)]" />
          <span className="text-[var(--color-ink-soft)]">
            Filtered to{' '}
            {search.pageUuid ? (
              <>
                page <span className="font-mono text-xs">{search.pageUuid}</span>
              </>
            ) : (
              <>
                site <span className="font-mono text-xs">{search.siteUuid}</span>
              </>
            )}
          </span>
          <button
            type="button"
            className="ml-auto text-xs underline"
            onClick={() => updateSearch({ pageUuid: undefined, siteUuid: undefined })}
          >
            Clear
          </button>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <form onSubmit={applyQ} className="flex items-center gap-2">
          <div className="relative">
            <MagnifyingGlass
              aria-hidden="true"
              size={14}
              className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-[var(--color-mute)]"
            />
            <Input
              value={searchText}
              onChange={(e) => setSearchText((e.target as HTMLInputElement).value)}
              placeholder="Search URL or audit id…"
              className="w-64 pl-7"
              aria-label="Search"
            />
          </div>
          <Button type="submit" variant="outline" size="sm">
            Apply
          </Button>
        </form>
        <Select
          value={search.status ?? 'any'}
          onValueChange={(v) => {
            if (typeof v === 'string') {
              updateSearch({ status: v === 'any' ? undefined : v });
            }
          }}
        >
          <SelectTrigger className="w-36">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="any">Any status</SelectItem>
            <SelectItem value="success">Success</SelectItem>
            <SelectItem value="error">Error</SelectItem>
            <SelectItem value="timeout">Timeout</SelectItem>
            <SelectItem value="running">Running</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {audits.isLoading && (
        <div className="space-y-2">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      )}

      {audits.isError && !audits.isLoading && (
        <ErrorState
          title="We couldn't load the audit log"
          error={audits.error}
          retry={() => {
            void audits.refetch();
          }}
        />
      )}

      {!audits.isLoading && !audits.isError && filtered.length === 0 && (
        <EmptyState
          icon={ListBullets}
          title="No audits match"
          description="Nothing here with the current filters. Clear them or run an audit to populate the log."
          action={
            search.pageUuid || search.siteUuid || search.status || search.q ? (
              <Button
                variant="outline"
                onClick={() => {
                  setSearchText('');
                  updateSearch({
                    pageUuid: undefined,
                    siteUuid: undefined,
                    status: undefined,
                    q: undefined,
                    from: undefined,
                    to: undefined,
                  });
                }}
              >
                Clear filters
              </Button>
            ) : undefined
          }
        />
      )}

      {!audits.isLoading && !audits.isError && filtered.length > 0 && (
        <section className="overflow-hidden rounded-2xl border border-[var(--color-line)] bg-[var(--color-paper)]">
          <AuditsTable
            audits={filtered}
            variant="full"
            renderRowAction={(a) => (
              <RowMenu
                audit={a}
                onRerun={async () => {
                  try {
                    await toastPromise(createAudit.mutateAsync({ body: { url: a.url } }), {
                      loading: 'Dispatching re-run…',
                      loadingDescription: a.url,
                      success: 'Re-run dispatched',
                    });
                  } catch {
                    // toastPromise already surfaced the error toast.
                  }
                }}
              />
            )}
          />
        </section>
      )}

      {audits.data && (audits.data.has_more || offset > 0) && (
        <div className="flex items-center justify-between pt-2">
          <span className="text-xs text-[var(--color-mute)]">
            Showing {offset + 1}–{offset + (audits.data.audits.length ?? 0)}
          </span>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={offset === 0}
              onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
            >
              ← Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={!audits.data.has_more}
              onClick={() => setOffset((o) => o + PAGE_SIZE)}
            >
              Next →
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function RowMenu({
  audit,
  onRerun,
}: {
  audit: AuditResponse;
  onRerun: () => Promise<void>;
}): ReactElement {
  async function copy(value: string, label: string) {
    try {
      await navigator.clipboard.writeText(value);
      toastSuccess(`Copied ${label}`);
    } catch {
      /* ignore */
    }
  }

  return (
    <Menu>
      <MenuTrigger render={<Button size="icon-sm" variant="ghost" aria-label="Row actions" />}>
        <DotsThreeVertical aria-hidden="true" />
      </MenuTrigger>
      <MenuPopup>
        <MenuItem
          onClick={() => {
            void copy(audit.id, 'audit id');
          }}
        >
          <Copy aria-hidden="true" />
          Copy audit id
        </MenuItem>
        {audit.ids?.snapshot_uuid && (
          <MenuItem
            onClick={() => {
              if (audit.ids) void copy(audit.ids.snapshot_uuid, 'snapshot uuid');
            }}
          >
            <Copy aria-hidden="true" />
            Copy snapshot uuid
          </MenuItem>
        )}
        {audit.report_url && (
          <MenuItem
            // biome-ignore lint/a11y/useAnchorContent: content lives in the MenuItem children which Base UI merges into the rendered <a>
            render={<a href={audit.report_url} target="_blank" rel="noreferrer noopener" />}
          >
            <DownloadSimple aria-hidden="true" />
            Download report
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            void onRerun();
          }}
        >
          <ArrowsClockwise aria-hidden="true" />
          Re-run
        </MenuItem>
      </MenuPopup>
    </Menu>
  );
}
